import Head from "next/head";
import Link from "next/link";
import React from "react";
import { useRouter } from "next/router";
import BackNavigation from "@/components/BackNavigation";
import { canonicalFromAsPath } from "@/lib/seo";

type LegalPageShellProps = {
  title: string;
  metaDescription?: string;
  lastUpdated: string;
  children: React.ReactNode;
};

type LegalCalloutProps = {
  tone?: "info" | "warning";
  children: React.ReactNode;
  className?: string;
};

export function LegalCallout({
  tone = "info",
  children,
  className = "",
}: LegalCalloutProps) {
  const tones =
    tone === "warning"
      ? "bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-500/40 dark:text-amber-100"
      : "bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/20 dark:border-blue-500/40 dark:text-blue-100";

  return (
    <div className={`mt-8 rounded-lg border p-4 ${tones} ${className}`}>
      <p className="text-sm leading-relaxed">{children}</p>
    </div>
  );
}

export default function LegalPageShell({
  title,
  metaDescription,
  lastUpdated,
  children,
}: LegalPageShellProps) {
  const router = useRouter();
  const canonicalUrl = canonicalFromAsPath(router.asPath);

  return (
    <>
      <Head>
        <title>{`${title} | Durmah`}</title>
        {metaDescription ? (
          <meta name="description" content={metaDescription} />
        ) : null}
        <link key="route-canonical" rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${title} | Durmah`} />
        {metaDescription ? (
          <meta property="og:description" content={metaDescription} />
        ) : null}
        <meta property="og:type" content="article" />
        <meta key="route-og-url" property="og:url" content={canonicalUrl} />
      </Head>

      <main className="mx-auto max-w-3xl px-4 py-12">
        <BackNavigation className="mb-8" forcePublic />

        <section className="rounded-2xl border border-gray-200/80 bg-white/85 p-6 sm:p-8 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.03]">
          <h1 className="mb-6 text-4xl font-bold text-purple-700 dark:text-purple-300">
            {title}
          </h1>

          <nav aria-label="Study rights and policies" className="mb-6 flex flex-wrap gap-x-5 gap-y-2 border-b border-slate-200 pb-4 text-sm text-blue-700 dark:border-slate-700 dark:text-blue-300">
            <Link href="/legal/responsible-study" className="underline">Responsible study &amp; student rights</Link>
            <Link href="/learn/private-study-copyright" className="underline">Material examples</Link>
            <Link href="/legal/service-transparency" className="underline">Availability &amp; data use</Link>
          </nav>

          <div className="space-y-4 text-base leading-7 text-gray-700 dark:text-gray-300 [&_h2]:scroll-mt-40 [&_li]:scroll-mt-40 [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-gray-900 dark:[&_h2]:text-gray-100 [&_ul]:mb-4 [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:space-y-2 [&_a]:text-blue-700 [&_a]:underline dark:[&_a]:text-blue-300 [&_strong]:text-gray-900 dark:[&_strong]:text-gray-100">
            {children}
          </div>

          <hr className="my-6 border-gray-300 dark:border-white/20" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Last updated: {lastUpdated}
          </p>
        </section>
      </main>
    </>
  );
}
